/**
 * Login Form Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import {
  formatColorFormat,
  formatComponentAlignmentFormat,
  formatResponsiveValue,
  formatSpacingFormat,
  formatTextAlignmentFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseSpacingFormat,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  ComponentAlignment,
  GradientDirection,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
} from '../../types'
import loginFormTemplate from './login-form.template.json'

export interface ILoginFormComponentSettings {
  // Design settings
  'login-form-design-h-align': ResponsiveValue<ComponentAlignment>
  'login-form-design-button-color-variant': string
  'login-form-design-field-shape': string
  'login-form-design-button-shape': string
  'login-form-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'login-form-design-button-size': ResponsiveValue<string>
  'login-form-design-button-padding': ResponsiveValue<SpacingInputValue>
  'login-form-design-margin': ResponsiveValue<SpacingInputValue>
  'login-form-design-typography': {
    'login-form-design-title-typography': ResponsiveValue<TypographyValue>
  }
  'login-form-design-group-color': ColorAdaptiveValue<string>
  'login-form-design-title-color': ColorAdaptiveValue<string>
  'login-form-design-title-color-solid': ColorAdaptiveValue<ColorVariant>
  'login-form-design-title-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'login-form-design-title-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'login-form-design-title-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'login-form-design-scheme': any
  'login-form-design-label-color': ColorAdaptiveValue<string>
  'login-form-design-label-color-solid': ColorAdaptiveValue<ColorVariant>
  'login-form-design-label-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'login-form-design-label-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'login-form-design-label-color-gradient-direction': ColorAdaptiveValue<GradientDirection>

  // Actions
  'login-form-action-on-login': BuilderActions
  'login-form-action-on-logout': BuilderActions

  // Behavior settings
  'login-form-behavior-redirect': boolean
}

export interface ILoginFormComponent extends IBuilderComponent {
  componentId: 'login-form'
  groupId: 'app'
  settings: ILoginFormComponentSettings
}

/**
 * Login Form AI Input
 */
export interface AiLoginFormInput {
  width?: string // Format: "w:100%"
  alignment?: string // Format: "center" or "left"
  margin?: string // Format: "m:24px"
  buttonColorVariant?: string // Button color variant
  buttonSize?: string // Format: "sm" or "md" or "lg"
  buttonPadding?: string // Format: "p:8px"
  buttonShape?: string // Format: "none", "small", "medium", "large"
  fieldShape?: string // Field corner radius
  typography?: {
    title?: string // Title typography
  }
  titleColor?: string // Format: "solid:primary-40" or "adaptive"
  labelColor?: string // Format: "solid:primary-40" or "adaptive"
  actionOnLogin?: BuilderActions
  actionOnLogout?: BuilderActions
  redirect?: boolean // Redirect after login
}

const LOGIN_FORM_DEFAULT_TEMPLATE = loginFormTemplate as unknown as ILoginFormComponentSettings

export class LoginFormComp {
  getTemplate(): ILoginFormComponentSettings {
    return LOGIN_FORM_DEFAULT_TEMPLATE
  }

  /**
   * format login form component settings to AI login form input
   */
  format(settings: ILoginFormComponentSettings): AiLoginFormInput {
    const result: AiLoginFormInput = {}

    // Design - Width (handle OverflowSettings)
    if (settings['login-form-design-width']) {
      const width = formatWidthFormat(settings['login-form-design-width'])
      if (width) {
        result.width = width
      }
    }

    // Design - Alignment
    if (settings['login-form-design-h-align']) {
      result.alignment = formatComponentAlignmentFormat(settings['login-form-design-h-align'])
    }

    // Design - Margin
    if (settings['login-form-design-margin']) {
      result.margin = formatSpacingFormat(settings['login-form-design-margin'])
    }

    // Design - Button Settings
    if (settings['login-form-design-button-color-variant']) {
      result.buttonColorVariant = settings['login-form-design-button-color-variant']
    }

    if (settings['login-form-design-button-size']) {
      const buttonSize = settings['login-form-design-button-size']
      const values = Object.values(buttonSize)
      if (values.length > 0) {
        const allSame = values.every((v) => v === values[0])
        if (allSame) {
          result.buttonSize = values[0]
        } else {
          result.buttonSize = formatResponsiveValue(buttonSize, (val) => String(val))
        }
      }
    }

    if (settings['login-form-design-button-padding']) {
      result.buttonPadding = formatSpacingFormat(settings['login-form-design-button-padding'])
    }

    if (settings['login-form-design-button-shape']) {
      result.buttonShape = settings['login-form-design-button-shape']
    }

    if (settings['login-form-design-field-shape']) {
      result.fieldShape = settings['login-form-design-field-shape']
    }

    // Design - Typography
    if (settings['login-form-design-typography']) {
      result.typography = {}
      if (settings['login-form-design-typography']['login-form-design-title-typography']) {
        result.typography.title = formatTypographyFormat(
          settings['login-form-design-typography']['login-form-design-title-typography']
        )
      }
    }

    // Design - Title Color
    if (settings['login-form-design-title-color']) {
      result.titleColor = formatColorFormat({
        colorType: settings['login-form-design-title-color'] as any,
        colorSolid: settings['login-form-design-title-color-solid'],
        colorStartGradient: settings['login-form-design-title-color-start-gradient'],
        colorEndGradient: settings['login-form-design-title-color-end-gradient'],
        colorGradientDirection: settings['login-form-design-title-color-gradient-direction'],
      })
    }

    // Design - Label Color
    if (settings['login-form-design-label-color']) {
      result.labelColor = formatColorFormat({
        colorType: settings['login-form-design-label-color'] as any,
        colorSolid: settings['login-form-design-label-color-solid'],
        colorStartGradient: settings['login-form-design-label-color-start-gradient'],
        colorEndGradient: settings['login-form-design-label-color-end-gradient'],
        colorGradientDirection: settings['login-form-design-label-color-gradient-direction'],
      })
    }

    // Behavior
    if (settings['login-form-behavior-redirect'] !== undefined) {
      result.redirect = settings['login-form-behavior-redirect']
    }

    return result
  }

  /**
   * parse AI login form input to login form component settings
   */
  parse(input: AiLoginFormInput): ILoginFormComponentSettings {
    const result = deepMerge(LOGIN_FORM_DEFAULT_TEMPLATE, {})

    // Design - Width
    if (input.width) {
      result['login-form-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignment = parseAlignmentFormat(input.alignment)
      if (alignment.cAlign) {
        result['login-form-design-h-align'] = alignment.cAlign
      }
    }

    // Design - Margin
    if (input.margin) {
      result['login-form-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Button Settings
    if (input.buttonColorVariant) {
      result['login-form-design-button-color-variant'] = input.buttonColorVariant
    }

    if (input.buttonSize) {
      const sizeMap: Record<string, string> = {
        sm: 'sm',
        md: 'md',
        lg: 'lg',
      }
      const size = sizeMap[input.buttonSize] || 'md'
      result['login-form-design-button-size'] = {
        phone: size,
        'phone-landscape': size,
        tablet: size,
        'tablet-landscape': size,
        desktop: size,
        tv: size,
      }
    }

    if (input.buttonPadding) {
      result['login-form-design-button-padding'] = parseSpacingFormat(input.buttonPadding)
    }

    if (input.buttonShape) {
      result['login-form-design-button-shape'] = input.buttonShape
    }

    if (input.fieldShape) {
      result['login-form-design-field-shape'] = input.fieldShape
    }

    // Design - Typography
    if (input.typography?.title) {
      const titleTypo = parseTypographyFormat(input.typography.title)
      result['login-form-design-typography']['login-form-design-title-typography'] = titleTypo
    }

    // Design - Title Color
    if (input.titleColor) {
      const colorParsed = parseColorFormat(input.titleColor)
      result['login-form-design-title-color'] = colorParsed.colorType
      result['login-form-design-title-color-solid'] = colorParsed.colorSolid
      result['login-form-design-title-color-start-gradient'] = colorParsed.colorStartGradient
      result['login-form-design-title-color-end-gradient'] = colorParsed.colorEndGradient
      result['login-form-design-title-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Label Color
    if (input.labelColor) {
      const colorParsed = parseColorFormat(input.labelColor)
      result['login-form-design-label-color'] = colorParsed.colorType
      result['login-form-design-label-color-solid'] = colorParsed.colorSolid
      result['login-form-design-label-color-start-gradient'] = colorParsed.colorStartGradient
      result['login-form-design-label-color-end-gradient'] = colorParsed.colorEndGradient
      result['login-form-design-label-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Actions
    if (input.actionOnLogin) {
      result['login-form-action-on-login'] = input.actionOnLogin
    }

    if (input.actionOnLogout) {
      result['login-form-action-on-logout'] = input.actionOnLogout
    }

    // Behavior
    if (input.redirect !== undefined) {
      result['login-form-behavior-redirect'] = input.redirect
    }

    return result
  }
}
